// The notifications bell (APPLE-SPEC §5.6, §5.8; shell-ia §8): a 28 px glyph button with a 7 px red dot at its top inline-end corner when
// something needs the person (the count is in `aria-label="Notifications: 7 need your decision"` and in the popover). Its popover (380 px, role="dialog"):
// the header "Inbox" with a segmented control `For me 7 | Department 23`, at most four rows — each row is the target itself (an unread
// dot, a 14 px glyph, a 13 / 500 title, a 12 px sub-line) — and "Open inbox ›" at the end of the footer (the counts are the control's).
// The rows are the person's own decisions and the steps waiting on them, from `GET /api/econic/inbox/summary?take=5`; when nothing needs a
// decision, the unread notifications are listed instead, so the popover is never empty while something is new. The badge counts what needs
// the person, never the department's backlog. The assignments waiting on the person (23 Oct 2026) sit on top in their own section
// (../assignments/BellSection.tsx) and count in the badge. WP-SHELL.
import { useState } from "react";
import { Bell, BellRing, ChevronLeft, ChevronRight, FileText, Footprints, LayoutGrid } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError, useApi, useAuth } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@ncp/ui";
import type { EconicInboxRow, EconicInboxSummary } from "@ncp/assistant-sdk/contracts";
import { SegmentedControl } from "../theme/primitives";
import { countText } from "../lib/format";
import { relativeTime } from "../lib/time";
import { INBOX_QUERY_KEY, useShellSummary } from "./useShellSummary";
import { useWorkspace } from "../../app/workspace-context";
import { useAssignmentCount, useAssignments } from "../assignments/api";
import { AssignmentsBellSection } from "../assignments/BellSection";

interface NotificationRow { id: string; kind: string; title: string; titleAr?: string | null; link?: string | null; createdAt: string; readAt?: string | null }
/** A row may carry its own sub-line (the desk or the requester); older servers send only the time and the department. */
type BellRow = EconicInboxRow & { subtitle?: { en: string; ar?: string | null } | null };

/** A stack without the summary still lists the person's notifications (real rows); decisions then live in the full inbox. */
function fromNotifications(rows: NotificationRow[]): EconicInboxSummary {
  return {
    decisions: { total: 0, atLeast: false, rows: [] },
    waiting: { total: 0, rows: [] },
    notifications: {
      total: rows.length,
      unread: rows.filter((r) => !r.readAt).length,
      rows: rows.slice(0, 5).map((r) => ({
        id: r.id, tab: "notifications", kind: r.kind, title: { en: r.title, ar: r.titleAr ?? r.title }, at: r.createdAt,
        href: r.link || "/inbox", action: "open", unread: !r.readAt,
      })),
    },
  };
}

export function useInboxSummary(enabled: boolean): { data: EconicInboxSummary | null; loading: boolean; partial: boolean; error: boolean } {
  const api = useApi();
  const { user } = useAuth();
  const q = useQuery({
    queryKey: [...INBOX_QUERY_KEY, user?.id ?? null],
    queryFn: async (): Promise<{ summary: EconicInboxSummary; partial: boolean }> => {
      try {
        return { summary: await api.get<EconicInboxSummary>("/api/econic/inbox/summary", { take: 5 }), partial: false };
      } catch (e) {
        if (!(e instanceof ApiError) || (e.status !== 404 && e.status !== 405)) throw e;
        const rows = await api.get<NotificationRow[]>("/api/notifications", { take: 20 });
        return { summary: fromNotifications(rows), partial: true };
      }
    },
    enabled: enabled && !!user,
    staleTime: 30_000,
  });
  return { data: q.data?.summary ?? null, loading: q.isLoading, partial: q.data?.partial ?? false, error: q.isError };
}

const ICON = { decisions: FileText, waiting: Footprints, notifications: BellRing } as const;
const MAX_ROWS = 4;

export function InboxBell() {
  const { t } = useTranslation();
  const { locale, dir } = useLocale();
  const ar = locale === "ar";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<"me" | "department">("me");
  const { data: shell } = useShellSummary();
  const inbox = useInboxSummary(open);
  // Assignments waiting on the person (Assignments, 23 Oct 2026) count with the decisions and steps waiting on them.
  const { can } = useWorkspace();
  const assignmentsOn = can("assistant.use");
  const assignmentCount = useAssignmentCount(assignmentsOn).data ?? 0;
  const counts = shell?.counts;
  const needs = counts ? counts.decisions + counts.waiting + assignmentCount : assignmentCount || null;
  const unread = counts?.notifications ?? 0;
  // The badge: what needs the person's decision; when nothing does, their unread notifications.
  const badge = needs ? needs : unread;
  const atLeast = needs ? counts?.decisionsAtLeast ?? false : false;
  const label = counts === undefined && !needs
    ? t("assistant:econic.shell.inbox.label")
    : needs
      ? t("assistant:econic.shell.inbox.badge", { count: needs, mine: countText(needs, "en", { cap: 99, atLeast }) })
      : unread
        ? t("assistant:econic.shell.inbox.badgeUnread", { count: unread })
        : t("assistant:econic.shell.inbox.label");

  const summary = inbox.data;
  const mineTotal = summary ? summary.decisions.total + summary.waiting.total + assignmentCount : needs ?? 0;
  const inboxNeeds = counts ? counts.decisions + counts.waiting : 0;
  const assignmentRows = useAssignments("action", 50, open && assignmentsOn).data?.length ?? 0;
  const deptTotal = summary?.department?.total ?? null;
  const decisionRows = summary ? [...summary.decisions.rows, ...summary.waiting.rows] : [];
  const notificationRows = summary ? summary.notifications.rows.filter((r) => r.unread) : [];
  const rows = (decisionRows.length ? decisionRows : notificationRows).slice(0, MAX_ROWS) as BellRow[];
  const listing = decisionRows.length ? "decisions" : notificationRows.length ? "notifications" : "none";
  // A person with decisions waiting still sees their newest unread notices under them (Today's "Notify me" matches, Signals alerts): two at most.
  const alsoNotices = listing === "decisions" ? (notificationRows.slice(0, 2) as BellRow[]) : [];
  // The department's rows are the full inbox's (the summary carries only its total).
  const showDept = scope === "department";

  const go = (href: string) => {
    setOpen(false);
    navigate(href);
  };
  const sub = (row: BellRow) => {
    const own = row.subtitle ? (ar ? row.subtitle.ar || row.subtitle.en : row.subtitle.en) : null;
    return [own ?? row.department ?? null, relativeTime(row.at, locale)].filter(Boolean).join(" · ");
  };
  const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setScope("me"); }}>
      <PopoverTrigger asChild>
        <button type="button" className="es-icon-btn" aria-label={label} title={t("assistant:econic.shell.inbox.title")} aria-haspopup="dialog" data-econic-bell="">
          <Bell aria-hidden />
          {badge ? <span className="dot" aria-hidden /> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} collisionPadding={8} className="es-pop es-inbox p-0" aria-label={t("assistant:econic.shell.inbox.title")}>
        <div className="nh">
          <b>{listing === "notifications" ? t("assistant:econic.shell.inbox.notifications") : t("assistant:econic.shell.inbox.title")}</b>
          {deptTotal !== null ? (
            <SegmentedControl<"me" | "department">
              label={t("assistant:econic.shell.inbox.scope")}
              value={scope}
              onChange={setScope}
              options={[
                { value: "me", label: t("assistant:econic.shell.inbox.forMe"), count: mineTotal },
                { value: "department", label: t("assistant:econic.shell.inbox.department"), count: deptTotal },
              ]}
            />
          ) : null}
        </div>
        <div className="flex max-h-[min(420px,60dvh)] flex-col overflow-y-auto">
          {/* the assignments waiting on the person, on top (their own section, up to five, "See all") */}
          {showDept || !assignmentsOn ? null : <AssignmentsBellSection enabled={open} onGo={go} />}
          {showDept ? (
            <p className="nempty">{t("assistant:econic.shell.inbox.deptInInbox", { count: deptTotal ?? 0 })}</p>
          ) : inbox.loading ? (
            <div className="flex flex-col gap-2 px-3.5 py-3" aria-hidden>{[0, 1, 2].map((i) => <span key={i} className="block h-[34px] rounded-[6px] bg-fill-2" />)}</div>
          ) : rows.length === 0 ? (
            // A summary that could not be read is not "nothing": the rows are then the full inbox's. With assignments listed above,
            // an empty inbox says nothing more.
            inbox.partial || inbox.error || (!summary && !!inboxNeeds) ? <p className="nempty">{t("assistant:econic.shell.inbox.inFullInbox")}</p>
              : assignmentRows ? null : <p className="nempty">{t("assistant:econic.shell.inbox.empty")}</p>
          ) : (
            [...rows, ...(showDept ? [] : alsoNotices.map((r, i) => ({ ...r, notice: i === 0 })))].map((row) => {
              const Icon = row.kind === "ui-change" ? LayoutGrid : ICON[row.tab] ?? FileText;
              const title = ar ? row.title.ar || row.title.en : row.title.en;
              const heading = "notice" in row && row.notice
                ? <p key={`${row.id}-h`} className="px-3.5 pb-0.5 pt-2.5 text-[12px] font-semibold leading-4" style={{ color: "var(--label-2)" }}>{t("assistant:econic.shell.inbox.notifications")}</p>
                : null;
              return [heading,
                <button key={row.id} type="button" className="ni" onClick={() => go(row.href)}>
                  <span className={`u ${row.unread ? "" : "no"}`} aria-hidden />
                  <Icon aria-hidden />
                  <span className="tx">
                    <span className="tt">
                      {row.unread ? <span className="sr-only">{t("assistant:econic.shell.inbox.unread")} · </span> : null}
                      {title}
                    </span>
                    <span className="st">{sub(row)}</span>
                  </span>
                </button>,
              ];
            })
          )}
        </div>
        <div className="nf">
          <span />
          <Link to={showDept ? "/inbox?scope=department" : "/inbox"} onClick={() => setOpen(false)}>
            {t("assistant:econic.shell.inbox.open")}
            <Chevron aria-hidden />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
